import React from "react";
import { resolveMarket } from "@/presentation/marketRegistry";
import { marketTypeRisk } from "@/presentation/riskModel";
import { teamIdentity, teamInitials } from "@/presentation/teamIdentity";
import { normalizeCompetition, countryInitials } from "@/presentation/competitionNormalizer";
import { countryFlagUrl } from "@/presentation/countryFlags";
import { leagueIdentity } from "@/presentation/leagueIdentity";
import { resolveResult, lifecycleOf, ResultKey } from "@/presentation/resultPresentation";
import { TIERS } from "@/presentation/membershipPresentation";
import { MembershipTier, SignalStatus } from "@/types/api";
import { MarketIcon } from "@/components/common/Icon";
import { useI18n } from "@/i18n";

// ---------------------------------------------------------------- MarketBadge
export function MarketBadge({
  code, backendLabel, size = "md",
}: { code: string | null; backendLabel?: string | null; size?: "sm" | "md" }) {
  const { lang } = useI18n();
  const m = resolveMarket(code, lang, backendLabel);
  const pad = size === "sm" ? "px-2 py-1 text-[11px]" : "px-2.5 py-1.5 text-xs";
  return (
    <span
      data-testid="market-badge"
      className={`inline-flex items-center gap-1.5 rounded-lg border border-atlas/25 bg-atlas/10 font-semibold text-atlas-300 ${pad}`}
    >
      <MarketIcon name={m.icon} className="h-3.5 w-3.5" />
      {m.label}
    </span>
  );
}

// ---------------------------------------------------------------- CompetitionLabel
export function CompetitionLabel({
  country, league, leagueLogo, className = "",
}: { country?: string | null; league?: string | null; leagueLogo?: string | null; className?: string }) {
  const c = normalizeCompetition(country, league);
  const flag = countryFlagUrl(c.country);
  const realLeagueLogo = leagueLogo && /^https?:\/\//i.test(leagueLogo) ? leagueLogo : null;
  const logo = realLeagueLogo || leagueIdentity(league).logoUrl;
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="competition-label">
      {logo ? (
        <img src={logo} alt={c.league} loading="lazy"
          className="h-6 w-6 shrink-0 rounded-md object-contain ring-1 ring-white/10" />
      ) : flag ? (
        <img src={flag} alt={c.country} loading="lazy"
          className="h-4 w-6 shrink-0 rounded-[3px] object-cover ring-1 ring-white/10" />
      ) : (
        <span className="grid h-6 w-8 shrink-0 place-items-center rounded-md border border-white/10 bg-white/5 text-[10px] font-bold tracking-wider text-muted-foreground">
          {countryInitials(c.country)}
        </span>
      )}
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold text-foreground/90">{c.country}</p>
        <p className="truncate text-[11px] text-muted-foreground">{c.league}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- ResultBadge / SignalStatus
export function ResultBadge({
  status, won, isLive = false, size = "md",
}: { status: SignalStatus | null; won?: number | boolean | null; isLive?: boolean; size?: "sm" | "md" }) {
  const { lang } = useI18n();
  const key = lifecycleOf(status, won ?? null);
  const r = resolveResult(key, isLive && key === "PENDING");
  const label = r[lang];
  const pad = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span
      data-testid={`result-badge-${r.key.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 rounded-full border font-bold uppercase tracking-wider ${r.text} ${r.bg} ${r.border} ${pad}`}
    >
      <span className="relative flex h-2 w-2">
        {(r.key === "LIVE") && <span className={`absolute inline-flex h-full w-full rounded-full ${r.dot} opacity-70 animate-ping-ring`} />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${r.dot} ${r.key === "LIVE" ? "animate-live-pulse" : ""}`} />
      </span>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------- MembershipBadge
export function MembershipBadge({ tier, className = "" }: { tier: MembershipTier; className?: string }) {
  const { lang } = useI18n();
  const t = TIERS[tier];
  return (
    <span
      data-testid={`membership-badge-${tier.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${t.chip} ${t.accent} ${className}`}
    >
      {t[lang]}
    </span>
  );
}

export type { ResultKey };

// Team crest: real provider crest when available (TeamIdentityProvider), otherwise
// a polished monogram. We never fabricate or scrape a crest.
export function TeamMono({ name, size = 28 }: { name: string; size?: number }) {
  const { crestUrl } = teamIdentity(name);
  const initials = teamInitials(name);
  if (crestUrl) {
    return (
      <img src={crestUrl} alt={name} loading="lazy"
        className="shrink-0 rounded-lg object-contain ring-1 ring-white/10"
        style={{ width: size, height: size }} />
    );
  }
  return (
    <span
      className="grid shrink-0 place-items-center rounded-lg border border-white/10 bg-white/[0.04] text-[11px] font-bold text-foreground/80"
      style={{ width: size, height: size }}
      aria-hidden
    >
      {initials}
    </span>
  );
}

// Transparent market-type risk chip. Clearly labelled as classification of the
// BET TYPE (not a fabricated per-match risk).
export function RiskChip({ code, size = "sm" }: { code: string | null; size?: "sm" | "md" }) {
  const { lang } = useI18n();
  const r = marketTypeRisk(code, lang);
  if (!r.available || !r.level) return null;
  const tone = r.level === "LOW" ? "border-atlas/25 bg-atlas/10 text-atlas-300"
    : r.level === "HIGH" ? "border-live/25 bg-live/10 text-live-400"
    : "border-gold/25 bg-gold/10 text-gold-300";
  const pad = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span data-testid={`risk-chip-${r.level.toLowerCase()}`}
      title={lang === "tr" ? "Market türüne göre" : "By market type"}
      className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-wider ${tone} ${pad}`}>
      {r.label}
    </span>
  );
}
